/*
 * Auto-gift: puts a free gift line in the cart while the promo window is open
 * and the cart holds a qualifying prepaid plan, and takes it back out when the
 * cart stops qualifying.
 *
 * Config comes from snippets/auto-gift-config.liquid as window.AutoGiftConfig.
 * The gift's price is zeroed by an automatic discount in admin, not here.
 *
 * Lines this script owns are tagged with the `_auto_gift` line item property.
 * The underscore keeps it hidden from the customer at cart and checkout, and
 * it is how we tell our line apart from a tumbler the customer chose to buy.
 */
(function () {
  const config = window.AutoGiftConfig;
  if (!config || !config.giftProductId || !(config.giftVariants || []).length) return;

  // Outside the window the script still runs, so that a gift we added on the
  // last night comes back out of the cart once its discount has ended.
  const startsAt = Date.parse(config.startsAt);
  const endsAt = Date.parse(config.endsAt);
  if (Date.now() < startsAt) return;
  const isOpen = () => Date.now() <= endsAt;

  const TRIGGER_IDS = new Set(config.triggerProductIds || []);
  const PLAN_TERMS = (config.planTerms || []).map((term) => term.toLowerCase());
  const GIFT_PROPERTY = '_auto_gift';
  // Set once we add a gift, so that a gift disappearing from a still-qualifying
  // cart can only mean the customer removed it. We do not re-add after that.
  const ADDED_KEY = 'autoGift:added';
  const DECLINED_KEY = 'autoGift:declined';

  let busy = false;

  function readFlag(key) {
    try {
      return sessionStorage.getItem(key) === '1';
    } catch (e) {
      return false;
    }
  }

  function writeFlag(key, value) {
    try {
      value ? sessionStorage.setItem(key, '1') : sessionStorage.removeItem(key);
    } catch (e) {
      /* private mode; the promo still works, it just forgets across reloads */
    }
  }

  /* Section ids the page can currently repaint, so add/change responses come
     back with fresh markup instead of needing a second round trip. */
  function sectionsToRequest() {
    const ids = new Set(['cart-icon-bubble']);
    document.querySelectorAll('cart-items, cart-drawer-items, cart-drawer, cart-notification').forEach((el) => {
      if (typeof el.getSectionsToRender !== 'function') return;
      el.getSectionsToRender().forEach((section) => ids.add(section.section));
    });
    return Array.from(ids);
  }

  function getCart() {
    return fetch(`${routes.cart_url}.js`, { headers: { 'Content-Type': 'application/json' } }).then((r) => r.json());
  }

  /* The cart icon bubble is the one target no other subscriber repaints on our
     behalf, so we handle it directly. Item lists refresh via the cartUpdate
     publish below, which is the theme's own resync path. */
  function paintCartBubble(sections) {
    const html = sections && sections['cart-icon-bubble'];
    const target = document.getElementById('cart-icon-bubble');
    if (!html || !target) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    if (parsed) target.innerHTML = parsed.innerHTML;
  }

  function addGift() {
    // Preferred colour first, then any other colour still in stock. A variant
    // that sold out since page render returns 422, so we fall through on error.
    const ordered = config.giftVariants
      .slice()
      .sort((a, b) => Number(b.available) - Number(a.available));

    const attempt = (index) => {
      if (index >= ordered.length) return null;

      const body = JSON.stringify({
        items: [
          {
            id: ordered[index].id,
            quantity: 1,
            properties: { [GIFT_PROPERTY]: 'true' },
          },
        ],
        sections: sectionsToRequest(),
        sections_url: window.location.pathname,
      });

      return fetch(routes.cart_add_url, { ...fetchConfig(), ...{ body } })
        .then((response) => response.json())
        .then((data) => {
          if (data.status) return attempt(index + 1); // out of stock, try next colour
          writeFlag(ADDED_KEY, true);
          return data;
        });
    };

    return attempt(0);
  }

  function removeGift(line) {
    const body = JSON.stringify({
      id: line.key,
      quantity: 0,
      sections: sectionsToRequest(),
      sections_url: window.location.pathname,
    });
    return fetch(routes.cart_change_url, { ...fetchConfig(), ...{ body } }).then((r) => r.json());
  }

  function sync(eventData) {
    if (busy) return Promise.resolve();
    busy = true;

    return getCart()
      .then((cart) => {
        const items = cart.items || [];

        // A subscription carries its term in the selling plan name ("6 months
        // prepaid"); a Gift Subscription is a one-time purchase that carries it
        // in the variant title ("6 months Prepaid / 2 bags").
        const qualifies = isOpen() && items.some((item) => {
          if (!TRIGGER_IDS.has(item.product_id)) return false;
          const plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
          const label = `${(plan && plan.name) || ''} ${item.variant_title || ''}`.toLowerCase();
          return PLAN_TERMS.some((term) => label.includes(term));
        });

        // Any tumbler line counts as "gift present" so we never stack a second
        // one on top of a tumbler the customer added themselves.
        const giftPresent = items.some((item) => item.product_id === config.giftProductId);
        const ourLine = items.find(
          (item) => item.product_id === config.giftProductId && item.properties && item.properties[GIFT_PROPERTY]
        );

        if (qualifies && !giftPresent) {
          if (readFlag(ADDED_KEY)) {
            // We put it there and it is gone: the customer took it out.
            writeFlag(DECLINED_KEY, true);
            writeFlag(ADDED_KEY, false);
            return null;
          }
          if (readFlag(DECLINED_KEY)) return null;
          return addGift();
        }

        if (!qualifies && ourLine) {
          writeFlag(ADDED_KEY, false);
          writeFlag(DECLINED_KEY, false);
          return removeGift(ourLine);
        }

        return null;
      })
      .then((result) => {
        if (!result) return;

        paintCartBubble(result.sections);

        // product-form repaints from the cartUpdate payload after subscribers
        // settle. Patch fresh sections into the payload so it paints the cart
        // including the gift rather than the pre-gift snapshot it fetched.
        // Sections only: /cart/add.js returns just the lines it added, so
        // copying the rest of the response over would corrupt the cart data
        // that other subscribers read.
        if (eventData && eventData.cartData && result.sections) {
          eventData.cartData.sections = {
            ...(eventData.cartData.sections || {}),
            ...result.sections,
          };
        }

        // Cart page and drawer item lists resync off this. Deferred so it lands
        // after the current render pass rather than racing it.
        return getCart().then((freshCart) => {
          setTimeout(() => {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'auto-gift', cartData: freshCart });
          }, 0);
        });
      })
      .catch((e) => {
        console.error('[auto-gift]', e);
      })
      .finally(() => {
        busy = false;
      });
  }

  subscribe(PUB_SUB_EVENTS.cartUpdate, (eventData) => {
    if (eventData && eventData.source === 'auto-gift') return; // our own publish
    return sync(eventData);
  });

  // Covers arriving on the cart with a qualifying cart already built, and
  // carts changed outside this tab.
  document.addEventListener('DOMContentLoaded', () => sync(null));
})();
